import {
	Card,
	CardContent,
	Typography,
	Button,
	Box,
	Tooltip,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "../../assets/images/check_circle.svg";

import FailedIcon from "../../assets/images/error.svg";
import DropdownMenu from "../DropdownMenu/DropdownMenu";
import frontendSettings from "../../../frontend_settings.json";
import { useSelector } from "react-redux";
import { RootState } from "../../store/appStore";

interface CardProps {
	contract_id: string;
	status: string;
	file_count: number;
	contract_workspace: string;
	comments?: string;
	is_shared?: boolean;
	onClickCard: (val: string, name: string, count: number) => void;
	onActionClicked: (val: string, id: string, comments: string | undefined, contract_workspace: string) => void;
}

const CardComponent = ({
	contract_id,
	comments,
	contract_workspace,
	status,
	file_count,
	is_shared,
	onClickCard,
	onActionClicked,
}: CardProps) => {
	const { grey, green, red } = frontendSettings.CWStatus;

	const userInfo: any = useSelector((state: RootState) => state.auth.user);
	const isAdmin = userInfo?.roles?.includes("Admin");

	const getStatusIcon = () => {
		switch (status) {
			case "UPLOADED":
				return <img src={CheckCircleIcon} alt="Complete" />;
			case "INGESTED":
				return <img src={CheckCircleIcon} alt="Complete" />;
			case "ANALYSED":
				return <img src={CheckCircleIcon} alt="Complete" />;
			case "INGESTION_FAILED":
				return <img src={FailedIcon} alt="Failed" />;
			case "ANALYSIS_FAILED":
				return <img src={FailedIcon} alt="Failed" />;
			case "INPROGRESS":
				return <img src={CheckCircleIcon} alt="Complete" />;
			default:
				return null;
		}
	};

	const handleMenuSelect = (item: any, id: any, comments: string | undefined, contract_workspace: string) => {
		onActionClicked(item, id, comments, contract_workspace);
	};
	const menuItems = [
		{ label: "Add Files", id: "add", disabled: false },
		{ label: "Contract Insights", id: "insight", disabled: status !== "ANALYSED" },
		{ label: "View Attributes", id: "attributes", disabled: status !== "ANALYSED" },
		...(isAdmin ? [
			{ label: "Generate Scenarios", id: "generate_scenarios", disabled: status !== "ANALYSED" },
			{ label: "Run Scenario", id: "run_scenario", disabled: status !== "ANALYSED" },
		] : []),
		{ label: "Share", id: "share", disabled: false },
		{ label: "Refresh", id: "refresh", disabled: false },
		{ label: "Update Comments", id: "update", disabled: false },
		{ label: "Delete", id: "delete", disabled: false },
	];

	const getStatusColor = (dotIndex: number) => {
		if (status === "EMPTY_WORKSPACE") return grey; // All gray

		if (status === "UPLOADED") {
			return dotIndex === 0 ? green : grey; // First dot green
		}

		if (status === "INGESTED" || status === "INGESTION_FAILED") {
			if (dotIndex === 0) return green; // First dot green
			if (dotIndex === 1)
				return status === "INGESTION_FAILED" ? red : green; // Second dot green or red
			return grey; // Third dot grey
		}

		if (status === "ANALYSED") {
			return green; // All dots green
		}

		if (status === "ANALYSIS_FAILED") {
			return dotIndex === 2 ? red : green; // First two dots green, last red
		}

		if (status === "INPROGRESS") {
			return dotIndex === 0 ? green : grey; // First dot green
		}

		return grey; // Default to grey
	};

	const StatusDot = ({ status }: { status: string }) => {
		return (
			<Box
				sx={{
					backgroundColor: status,
					width: 20,
					height: 20,
					borderRadius: "50%",
				}}
			/>
		);
	};
	interface StatusMapping {
		label: string;
		tooltip: string;
	}

	const statusMapper: Record<string, StatusMapping> = {
		EMPTY_WORKSPACE: {
			label: "Workspace is empty",
			tooltip: "The workspace is currently empty.",
		},
		UPLOADED: {
			label: "Uploaded",
			tooltip: "Files uploaded, now being ingested by AI",
		},
		INGESTED: {
			label: "Ingested",
			tooltip: "Files ingested by AI, now AI conducting analysis",
		},
		INGESTION_FAILED: {
			label: "Failed",
			tooltip: "File processing failure, please see workspace for details",
		},
		ANALYSED: {
			label: "Complete",
			tooltip: "AI-based analysis completed",
		},
		ANALYSIS_FAILED: {
			label: "Failed",
			tooltip: "AI-based analysis failure, please contact your admin",
		},
		INPROGRESS: {
			label: "In Progress",
			tooltip: "Analysis in progress",
		},
	};

	// Function to get status information based on backend status
	const getStatusInfo = (status: string) => {
		return (
			statusMapper[status] || {
				label: "Unknown Status",
				tooltip: "",
				color: "grey",
			}
		);
	};

	return (
		<Card
			variant="outlined"
			sx={{
				height: "230px",
				width: "300px",
				position: "relative",
				borderRadius: "8px",
				padding: "4px",
			}}
		>
			<CardContent>
				{/* Top Section */}
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
				>
					<Box display="flex" alignItems="center" gap={1}>
						{getStatusIcon()}
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						{is_shared && (
							<Tooltip title="Shared workspace" arrow placement="top">
								<GroupIcon fontSize="medium" color="primary" />
							</Tooltip>
						)}
						<DropdownMenu
							menuItems={menuItems}
							onSelect={(item: any) =>
								handleMenuSelect(item, contract_id, comments, contract_workspace)
							}
							edit={false}
						/>
					</Box>
				</Box>

				{/* Title & Description */}
				<Box sx={{ my: 1 }}>
					<Box display="flex" alignItems="center">
						<Typography variant="h6" noWrap sx={{ mr: 1 }}>
							<Tooltip
								title={contract_workspace}
								arrow
								placement="top"
							>
								<span>{contract_workspace}</span>
							</Tooltip>
						</Typography>
					</Box>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							overflow: 'auto',
							height: '58px',
							whiteSpace: 'normal',
							wordWrap: 'break-word',
						}}
					>
						{comments ? comments : '-'}
					</Typography>
				</Box>

				{/* Button */}
				<Box display="flex" justifyContent="space-between">
					<Box>
						{/* Status Indicator */}
						<Box display="flex" alignItems="center" gap={1} my={1}>
							<Tooltip
								title={getStatusInfo(status).tooltip}
								arrow
								placement="top"
							>
								<Box display="flex" gap={1}>
									<StatusDot status={getStatusColor(0)} />
									<StatusDot status={getStatusColor(1)} />
									<StatusDot status={getStatusColor(2)} />
								</Box>
							</Tooltip>
						</Box>
						<Typography variant="body2">
							{getStatusInfo(status).label}
						</Typography>
					</Box>
					<Box>
						<Button
							variant="contained"
							onClick={() => {
								onClickCard(
									contract_id,
									contract_workspace,
									file_count
								);
							}}
							color="primary"
						>
							View
						</Button>
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
};

export default CardComponent;
